import { Alert as MantineAlert, LoadingOverlay as MantineLoadingOverlay, Tooltip as MantineTooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import * as React from "react";
import { Badge, ThemeIcon } from "../primitives/indicators";
import { Flex } from "../primitives/layout";
import type { ElementProps, SizeValue, StyleProps } from "../primitives/style-props";

/** Where the floating element sits relative to its target. */
export type FloatingSide = "top" | "bottom" | "left" | "right";
export type FloatingPlacement = FloatingSide | `${FloatingSide}-start` | `${FloatingSide}-end`;

export interface TooltipProps extends Omit<ElementProps<"div">, "color">, StyleProps {
  /** The tooltip body. Required — a tooltip with nothing to say is a bug, not a default. */
  label: React.ReactNode;
  /**
   * Exactly one element. Mantine clones the child to attach the trigger ref and throws when it is
   * not one (`@mantine/core/esm/components/Tooltip/Tooltip.mjs:135`), so
   * `ReactElement` — not `ReactNode` — is the honest type: a string, array or null would otherwise
   * typecheck here and fail at runtime.
   */
  children: React.ReactElement;
  position?: FloatingPlacement;
  withArrow?: boolean;
  arrowSize?: number;
  arrowRadius?: number;
  /** Allows the label to wrap; pair with `width`. */
  multiline?: boolean;
  width?: number | "auto";
  openDelay?: number;
  offset?: number;
  /** Suppresses the tooltip without unmounting the target. */
  disabled?: boolean;
  /** Which interactions open it. Mantine 6 opens on hover only unless this says otherwise. */
  events?: { hover: boolean; focus: boolean; touch: boolean };
  /** Controlled open state; leave undefined for the usual uncontrolled behaviour. */
  opened?: boolean;
  /**
   * Renders the floating element in a portal on `document.body` instead of inline next to the
   * target. Inline is what Mantine 6 does by default, and it is what an ancestor with
   * `overflow: hidden` clips — 76 call sites pass `true` for exactly that reason and not one
   * passes `false`.
   *
   * Defaults to `true` here (SC-2950), against Mantine 6's `false`. Every call site that ever
   * expressed a preference asked for the portal — 76 pass `true`, none passes `false` — and both
   * wrappers this repo already shipped force it on (`TruncatedTextWithTooltip`, `RuleInfoTooltip`).
   * Mantine 7 makes portalling its own default, so this is also where the upgrade lands.
   * A call site can still opt out with `withinPortal={false}`; both directions stay pinned by the
   * feedback DOM test.
   */
  withinPortal?: boolean;
  /**
   * A class applied to the floating tooltip element. `className` cannot reach it — Mantine puts
   * that on the trigger child — so the two call sites that restyle the tooltip body (a white card
   * instead of the default dark chip) need this narrow owned prop. Same shape as the field
   * controls' `descriptionProps`: one owned name forwarding one class, not the vendor's
   * `classNames` map.
   */
  tooltipClassName?: string;
}

/**
 * Mantine 7 types Tooltip's ref as its floating `div`, but merges it onto the cloned trigger child
 * (`Tooltip.mjs`: `useMergedRef(tooltip.reference, getRefProp(children), ref)`), so the owned
 * `HTMLElement` ref is the honest one. It also removed `width` (SC-2703): the root takes the `w`
 * style prop it became. `feedback-casts.test.ts` pins every forwarded key and the `w` target to
 * Mantine's own props; the feedback DOM test proves the ref lands on the trigger.
 */
type TooltipRootProps = Omit<TooltipProps, "tooltipClassName" | "width"> & {
  classNames?: { tooltip: string };
  readonly ref?: React.Ref<HTMLElement>;
};
const TooltipRoot = MantineTooltip as React.ComponentType<TooltipRootProps>;

export const Tooltip = React.forwardRef<HTMLElement, TooltipProps>(function Tooltip(
  { children, tooltipClassName, width, withinPortal = true, ...props },
  ref,
) {
  // Mantine 6 set `width` on the tooltip element; an explicit `w` still wins, as its inline style did.
  return (
    <TooltipRoot
      {...props}
      w={props.w ?? width}
      withinPortal={withinPortal}
      classNames={tooltipClassName ? { tooltip: tooltipClassName } : undefined}
      ref={ref}
    >
      {children}
    </TooltipRoot>
  );
});

export interface AlertProps extends Omit<ElementProps<"div">, "title" | "color">, StyleProps {
  children?: React.ReactNode;
  title?: React.ReactNode;
  /** Theme colour key. "red" and "yellow" carry 174 of the 279 call sites. */
  color?: string;
  icon?: React.ReactNode;
  variant?: "light" | "filled" | "outline";
  radius?: SizeValue;
  /** Only one call site dismisses an Alert; the pair stays on the surface for it. */
  withCloseButton?: boolean;
  onClose?: () => void;
  /**
   * Classes for Alert's inner elements. `className` reaches only the root, so without these a
   * consumer has to select the inner elements structurally (`> div > div:last-child`), which binds
   * it to nesting depth and child order rather than to a name. Mantine keeps these slot names in
   * v7 while the structure moves, so routing through them is what keeps the upgrade inside
   * `src/ui`. Same owned-name-forwarding-one-class shape as `tooltipClassName`.
   */
  wrapperClassName?: string;
  bodyClassName?: string;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { children, wrapperClassName, bodyClassName, ...props },
  ref,
) {
  const slotClassNames = wrapperClassName || bodyClassName ? { wrapper: wrapperClassName, body: bodyClassName } : undefined;

  // Mantine 6 always rendered the message element that the root's `aria-describedby` names; Mantine 7
  // renders it only for truthy children (`Alert.mjs`). The fragment is always truthy, so a title-only
  // Alert keeps that element and the `xs` gap below its title (SC-2703).
  return (
    <MantineAlert {...props} classNames={slotClassNames} ref={ref}>
      <>{children}</>
    </MantineAlert>
  );
});

export interface LoadingOverlayProps extends ElementProps<"div">, StyleProps {
  visible: boolean;
  /** 0 shows the loader over unobscured content; Mantine's own default is 0.75. */
  overlayOpacity?: number;
  overlayBlur?: number | string;
  loaderProps?: { size?: Exclude<SizeValue, number>; color?: string };
  transitionDuration?: number;
}

/**
 * Mantine 7 moved the overlay's opacity and blur into `overlayProps` (`overlayOpacity` became
 * `backgroundOpacity`) and the duration into `transitionProps`; the owned names are unchanged
 * (SC-2703). An omitted value stays out of the object so Mantine's own default still applies — its
 * `overlayProps` merge would otherwise take an explicit `undefined` over the 0.75 opacity default.
 */
export const LoadingOverlay = React.forwardRef<HTMLDivElement, LoadingOverlayProps>(function LoadingOverlay(
  { overlayOpacity, overlayBlur, transitionDuration, ...props },
  ref,
) {
  const overlayProps = {
    ...(overlayOpacity === undefined ? {} : { backgroundOpacity: overlayOpacity }),
    ...(overlayBlur === undefined ? {} : { blur: overlayBlur }),
  };
  const transitionProps = transitionDuration === undefined ? undefined : { duration: transitionDuration };

  return <MantineLoadingOverlay {...props} overlayProps={overlayProps} transitionProps={transitionProps} ref={ref} />;
});

export interface InfoTooltipProps {
  children: React.ReactNode;
  label: string;
}

/**
 * The barrel's original seed, now implemented here rather than re-exported from `src/components`
 * (SC-2697). Its behaviour is unchanged: the caption sits beside an info icon that carries the
 * tooltip, and the tooltip takes the wrapper's defaults.
 */
export const InfoTooltip = ({ children, label }: InfoTooltipProps): React.JSX.Element => {
  return (
    <Flex align="center" gap="xs">
      {children}
      <Tooltip label={label}>
        <IconInfoCircle color="gray" size={16} style={{ marginTop: "-1px" }} />
      </Tooltip>
    </Flex>
  );
};

/** The one hover copy every beta mark shows, so each beta feature explains itself the same way. */
const BETA_FEEDBACK_COPY = "This is an experimental feature, evolving weekly. Please share your feedback with our team!";
/**
 * The one colour every beta mark uses. The badge's light label and the dot's filled disc both resolve
 * this theme colour at its primary shade, so the label and the dot show the same violet.
 */
const BETA_COLOR = "violet";

/** Marks a feature as beta with a "Beta" label, for page headers and other roomy spots. */
export const BetaBadge = (): React.JSX.Element => {
  return (
    <Tooltip label={BETA_FEEDBACK_COPY}>
      <Badge size="sm" variant="light" color={BETA_COLOR}>
        Beta
      </Badge>
    </Tooltip>
  );
};

/** The compact beta mark: a small dot in the badge's violet with the same hover copy, for tight spots such as a navigation link. */
export const BetaDot = (): React.JSX.Element => {
  return (
    <Tooltip label={BETA_FEEDBACK_COPY}>
      <ThemeIcon size={8} radius="xl" variant="filled" color={BETA_COLOR} role="img" aria-label="Beta">
        {null}
      </ThemeIcon>
    </Tooltip>
  );
};
